import type { PluginActionMessage, PluginContext, ToolView } from "@scoach/types";
import { useEffect, useRef } from "react";

/**
 * Renders a developer plugin in a sandboxed iframe and runs the postMessage
 * bridge with it. Security model:
 *  - The iframe is sandboxed (no `allow-same-origin`) → it has an opaque origin
 *    and cannot touch Sally's DOM/cookies. Its own origin is already isolated
 *    (cross-origin to Sally). Only admin-approved origins can even load here
 *    (server CSP frame-src allowlist).
 *  - Inbound messages are validated by `event.source === iframe.contentWindow`
 *    and the per-mount `nonce`; actions must be in the plugin's approved set.
 *  - We only ever send the `context` the caller passes (already filtered to the
 *    plugin's data scope).
 */
export function PluginHost({
  plugin,
  context,
  onAction,
  height = "100%",
}: {
  plugin: ToolView;
  context: PluginContext;
  onAction?: (msg: PluginActionMessage) => void;
  height?: number | string;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const nonceRef = useRef<string>(Math.random().toString(36).slice(2) + Date.now().toString(36));
  const readyRef = useRef(false);
  // Keep the latest context/handler in refs so the message listener + updates
  // don't need to re-bind on every render.
  const contextRef = useRef(context);
  contextRef.current = context;
  const actionRef = useRef(onAction);
  actionRef.current = onAction;

  const capabilities = plugin.actions ?? [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: bind once per plugin
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.source !== iframeRef.current?.contentWindow) return; // must be our frame
      // The iframe is sandboxed WITHOUT allow-same-origin → it has an opaque
      // origin, so its messages arrive with origin "null". Anything else did not
      // come from our sandboxed plugin frame; reject it.
      if (e.origin !== "null") return;
      const data = e.data as PluginActionMessage | undefined;
      if (!data || data.type !== "sally:action" || data.nonce !== nonceRef.current) return;
      if (!capabilities.includes(data.action)) return; // not an approved action
      actionRef.current?.(data);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [plugin.id]);

  function post(type: "sally:init" | "sally:update") {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    // targetOrigin MUST be "*" here: the iframe is sandboxed without
    // allow-same-origin, so it has an opaque origin that a specific targetOrigin
    // would never match (the message would be silently dropped). The sandbox
    // (opaque origin, no DOM/cookie access) + admin approval + security scan are
    // the controls; the recipient is the frame we set to plugin.embedUrl.
    if (type === "sally:init") {
      win.postMessage(
        { type, nonce: nonceRef.current, capabilities, context: contextRef.current },
        "*",
      );
    } else {
      win.postMessage({ type, context: contextRef.current }, "*");
    }
  }

  // Stream context updates to the plugin once it's loaded.
  // biome-ignore lint/correctness/useExhaustiveDependencies: post reads latest via ref
  useEffect(() => {
    if (readyRef.current) post("sally:update");
  }, [context]);

  return (
    <iframe
      ref={iframeRef}
      src={plugin.embedUrl}
      title={plugin.name}
      onLoad={() => {
        readyRef.current = true;
        post("sally:init");
      }}
      // Deliberately NO allow-same-origin (opaque origin — can't touch Sally's
      // DOM/cookies). allow-scripts for interactivity, forms/popups for typical
      // widget UX. NOT allow-popups-to-escape-sandbox: popups stay sandboxed so a
      // plugin can't spawn an un-sandboxed window.
      sandbox="allow-scripts allow-forms allow-popups"
      referrerPolicy="strict-origin-when-cross-origin"
      style={{ width: "100%", height, border: "none", borderRadius: 10, background: "#fff" }}
    />
  );
}
